/**
 * Phase 8: Perform View Panel - replaces flow/slot details panel in perform mode.
 * Layout: upper 4 buttons (CAM/OUTPUT/REMOTE/MEDIA modals), middle section (contributors + media),
 * lower sticky controls (mode switch, Preload/Transition; live context via Control tab switch).
 */

import { Box } from '@mui/material';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { getObjectId, resolveLiveBindingFlowId } from '@/helpers/object.id.helper';
import { FlowModeSection } from '../../flow-mode-section';
import { useFlowModeSwitching } from '../../use-flow-mode-switching';
import Widget from '@/components/common/widget';
import { useTranslations } from '@/i18n/use-translations';
import { PerformUpperButtons } from './perform-upper-buttons';
import { PerformMiddleSection } from './perform-middle-section';
import { PerformLowerControls } from './perform-lower-controls';
import { useGetLiveContextStateQuery } from '@/state/queries/player/use-get-live-context-state';
import { FlowControlLiveStateProvider } from '../../flow-control-live-state';
import {
  getEntityRefFromFlow,
  getOnAirLivekitRoomIdFromFlow,
  resolveSocketEntityFromFlow,
} from '@/helpers/entity-ref.helper';
import { useQueryClient } from '@tanstack/react-query';
import { usePlayerSocket } from '@/hooks/use-player-socket';
import { GET_LIVE_CONTEXT_STATE_QUERY_KEY } from '@/state/queries/player/use-get-live-context-state';
import { GET_LIVE_LANGUAGE_STATUS_QUERY_KEY } from '@/state/queries/player/use-live-language-status';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { invalidateSimulationCueSheet } from '@/state/queries/player/use-get-simulation-cue-sheet';
import { GET_PLAYERS_QUERY_KEY } from '@/state/queries/players/use-get-players';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import { shouldRefetchQueriesOnLiveContextInvalidated } from '@/helpers/should-refetch-on-live-context-invalidated.helper';

export interface PerformViewPanelProps {
  selectedSlot?: FlowSlotDto;
  eventSlotIdParam?: string | null;
  /** When true, hides the mode switcher (1 2 3) - used when panel is always in perform mode (e.g. FlowControlTab) */
  hideModeSection?: boolean;
}

export function PerformViewPanel({ selectedSlot, eventSlotIdParam, hideModeSection = false }: PerformViewPanelProps) {
  const t = useTranslations();
  const flow = useFlowStore((s) => s.flow);
  const queryClient = useQueryClient();
  const planFlowId = flow?.origin ? getObjectId(flow.origin) : flow?.id;
  const bindingFlowId = flow ? resolveLiveBindingFlowId(flow, eventSlotIdParam ?? undefined) : undefined;
  const entityRef = bindingFlowId ? getEntityRefFromFlow(bindingFlowId, flow?.liveContext) : undefined;
  const livekitRoomId = bindingFlowId ? getOnAirLivekitRoomIdFromFlow(bindingFlowId, flow?.liveContext) : undefined;
  const socketEntity = flow ? resolveSocketEntityFromFlow(flow, eventSlotIdParam ?? undefined) : undefined;

  const { currentMode, hasPlanMode, hasOrganizeMode, handleModeChange, flowToUse } = useFlowModeSwitching({
    flowId: planFlowId ?? undefined,
    flow: flow ?? null,
  });

  const { data: liveContextState, dataUpdatedAt: liveContextQueryUpdatedAt } = useGetLiveContextStateQuery(entityRef, {
    enabled: !!entityRef && !!flow?.liveContext?.isRunning,
  });

  usePlayerSocket(
    socketEntity?.entityId ?? '',
    socketEntity?.entityType ?? 'Flow',
    {
      onPlayersInvalidated: (entityId, entityType) => {
        queryClient.invalidateQueries({ queryKey: [GET_PLAYERS_QUERY_KEY, entityId, entityType] });
      },
      onLiveContextInvalidated: (_entityId, _entityType, meta) => {
        if (
          !shouldRefetchQueriesOnLiveContextInvalidated({
            liveIsRunning: Boolean(flow?.liveContext?.isRunning),
            isRunningFromEvent: meta?.isRunning,
          })
        ) {
          return;
        }
        if (entityRef) {
          queryClient.invalidateQueries({ queryKey: [GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef] });
          void queryClient.refetchQueries({ queryKey: [GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef] });
        }
        if (planFlowId) {
          void queryClient.refetchQueries({ queryKey: [GET_FLOW_QUERY_KEY, planFlowId] });
        }
        if (!flow?.liveContext?.isRunning) {
          void invalidateSimulationCueSheet(queryClient);
        }
      },
      onLiveLanguageStatus: () => {
        if (entityRef) {
          void queryClient.invalidateQueries({ queryKey: [GET_LIVE_LANGUAGE_STATUS_QUERY_KEY, entityRef] });
        }
      },
    },
    !!socketEntity?.entityId,
  );

  if (!flow) return null;

  return (
    <FlowControlLiveStateProvider
      entityRef={entityRef}
      performMode={flow.liveContext?.isRunning ? 'play' : 'simulation'}
    >
      <Box width="100%" sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {!hideModeSection && (
          <Widget
            title={t('flows.mode', { defaultValue: 'BRITESTAGE process' })}
            sx={{
              'borderRadius': 0,
              '& .MuiCardContent-root:last-child': { paddingBottom: 1 },
            }}
            showTitle={false}
          >
            <FlowModeSection
              currentMode={currentMode}
              hasPlanMode={hasPlanMode}
              hasOrganizeMode={hasOrganizeMode}
              handleModeChange={handleModeChange}
              flow={flowToUse}
              eventSlotIdParam={eventSlotIdParam}
              context="flowDetailSchedule"
              showDivider={false}
              sx={{ px: 0.5 }}
            />
          </Widget>
        )}

        {/* Upper 4 buttons: CAM Views, OUTPUT Views, REMOTE Views, MEDIA Shares */}
        {flow.liveContext?.isRunning && (
          <PerformUpperButtons
            flow={flow}
            selectedSlot={selectedSlot}
            eventSlotId={eventSlotIdParam ?? undefined}
            livekitRoomId={livekitRoomId}
            liveContextState={liveContextState}
          />
        )}

        {/* Middle section: Contributors + Media with selection UI */}
        {flow.liveContext?.isRunning && (
          <PerformMiddleSection
            flow={flow}
            selectedSlot={selectedSlot}
            entityRef={entityRef}
            eventSlotId={eventSlotIdParam ?? undefined}
            livekitRoomId={livekitRoomId}
            liveContextState={liveContextState}
            liveContextQueryUpdatedAt={liveContextQueryUpdatedAt}
          />
        )}

        {/* Lower sticky controls (show mode, preload); live context is toggled on the Control tab */}
        {entityRef && (
          <PerformLowerControls
            flow={flow}
            entityRef={entityRef}
            eventSlotId={eventSlotIdParam ?? undefined}
            liveContextState={liveContextState}
            liveContextQueryUpdatedAt={liveContextQueryUpdatedAt}
          />
        )}
      </Box>
    </FlowControlLiveStateProvider>
  );
}
